import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { QuoteRequest } from '@shared/api-types.js';

const MINE = (role: 'renter' | 'owner' | 'both') => ['bookings', 'mine', role] as const;
const ONE = (id: string) => ['bookings', 'one', id] as const;

export function useQuote(input: QuoteRequest | null) {
  return useQuery({
    queryKey: ['bookings', 'quote', input],
    queryFn: () => api.transactions.quote(input!),
    enabled: !!input,
    staleTime: 30_000,
  });
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuoteRequest & { message?: string }) =>
      api.transactions.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useCheckout(id: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('no_transaction_id');
      return api.transactions.checkout(id);
    },
  });
}

export function useMyBookings(role: 'renter' | 'owner' | 'both' = 'both') {
  return useQuery({
    queryKey: MINE(role),
    queryFn: () => api.transactions.mine(role),
    // Light polling so the Inbox unread-count badge stays fresh while the tab
    // is in focus. Stops when the tab is backgrounded.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useBooking(id: string | undefined) {
  return useQuery({
    queryKey: id ? ONE(id) : ['bookings', 'one', 'none'],
    queryFn: () => api.transactions.get(id!),
    enabled: !!id,
    // Pull new messages / state changes every 30s while the booking detail
    // page is open. Cheap — single row + messages.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useTransitionBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, reason }: { id: string; name: string; reason?: string }) =>
      api.transactions.transition(id, name, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ONE(vars.id) });
      qc.invalidateQueries({ queryKey: ['bookings', 'mine'] });
    },
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.transactions.sendMessage(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ONE(vars.id) });
    },
  });
}
